// The review values as the Swift encodes them (SuiteSync/Review.swift, SuiteModel/ReaderRound.swift), so the Mac can
// read what the web writes and the other way round:
//   - `ReviewCopy`, `ReviewNote` and the `ReaderProgress` a reader sends go through `JSONEncoder.review`: sorted keys and
//     Foundation's default date encoding, a number of seconds since 2001-01-01T00:00:00Z (`timeIntervalSinceReferenceDate`).
//   - What the project keeps (`settings.readerProgress`, `settings.readerRounds`, and the web's `settings.webReaderRounds`)
//     uses sorted keys and `.iso8601` dates, which Foundation writes and reads without fractional seconds.
// Optionals are left out when nil, as `encodeIfPresent` does. Values from a reader are untrusted: every decode checks types.

/** Seconds between the Unix epoch and Foundation's reference date. */
export const REFERENCE_DATE_OFFSET = 978_307_200;

export const toReferenceSeconds = (d: Date): number => d.getTime() / 1000 - REFERENCE_DATE_OFFSET;
export const fromReferenceSeconds = (s: number): Date => new Date((s + REFERENCE_DATE_OFFSET) * 1000);

/** `.iso8601` as Foundation writes it (`ISO8601DateFormatter`, internet date-time: whole seconds, `Z`). */
export const toISO8601 = (d: Date): string => d.toISOString().replace(/\.\d{3}Z$/, 'Z');

/** `.iso8601` as Foundation reads it: whole seconds only (a fractional one is refused, as the Swift would). */
export function fromISO8601(s: unknown): Date {
  if (typeof s !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(Z|[+-]\d{2}:?\d{2})$/.test(s)) throw new ReviewDataError('not an ISO 8601 date');
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) throw new ReviewDataError('not an ISO 8601 date');
  return d;
}

export class ReviewDataError extends Error {}

/** JSON with every object's keys sorted (`.sortedKeys`); dates as `date` says. */
export function sortedJSON(value: unknown, date: (d: Date) => string | number): string {
  const norm = (v: unknown): unknown => {
    if (v instanceof Date) return date(v);
    if (Array.isArray(v)) return v.map(norm);
    if (v && typeof v === 'object') {
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(v).sort()) {
        const x = (v as Record<string, unknown>)[k];
        if (x !== undefined && x !== null) out[k] = norm(x);
      }
      return out;
    }
    return v;
  };
  return JSON.stringify(norm(value));
}

// Checks for untrusted JSON ----------------------------------------------------------------------------------------

export function object(v: unknown, what: string): Record<string, unknown> {
  if (!v || typeof v !== 'object' || Array.isArray(v)) throw new ReviewDataError(`${what} isn’t an object`);
  return v as Record<string, unknown>;
}

export function string(o: Record<string, unknown>, key: string): string {
  const v = o[key];
  if (typeof v !== 'string') throw new ReviewDataError(`${key} isn’t a string`);
  return v;
}

export function optionalString(o: Record<string, unknown>, key: string): string | undefined {
  const v = o[key];
  if (v === undefined || v === null) return undefined;
  if (typeof v !== 'string') throw new ReviewDataError(`${key} isn’t a string`);
  return v;
}

export function number(o: Record<string, unknown>, key: string): number {
  const v = o[key];
  if (typeof v !== 'number' || !Number.isFinite(v)) throw new ReviewDataError(`${key} isn’t a number`);
  return v;
}

/** A `UInt64`/`Int` the Swift would decode: a whole number in range. */
export function whole(o: Record<string, unknown>, key: string, min = 0): number {
  const v = number(o, key);
  if (!Number.isInteger(v) || v < min || v > Number.MAX_SAFE_INTEGER) throw new ReviewDataError(`${key} isn’t a whole number`);
  return v;
}

export function boolean(o: Record<string, unknown>, key: string): boolean {
  const v = o[key];
  if (typeof v !== 'boolean') throw new ReviewDataError(`${key} isn’t true or false`);
  return v;
}

export function referenceDate(o: Record<string, unknown>, key: string): Date {
  return fromReferenceSeconds(number(o, key));
}
